/**
 * Staggered LuxPlus+ renewal reward identifiers. The reward a member earns escalates
 * with each consecutive renewal year: year 1 → LuxPlusRenewal, year 2 → LuxPlusRenewal2YR,
 * year 3 and later → LuxPlusRenewal3YR.
 *
 * svc-lux-loyalty holds a distinct action-reward config per identifier (with its own
 * bonus points / status credits), so the frontend selects the member's current
 * identifier and requests its earn to display the correct staggered bonus.
 */
export const LUX_PLUS_RENEWAL_ACTIONS = [
  'LuxPlusRenewal',
  'LuxPlusRenewal2YR',
  'LuxPlusRenewal3YR',
] as const

export type LuxPlusRenewalAction = (typeof LUX_PLUS_RENEWAL_ACTIONS)[number]

/**
 * Fallback renewal-bonus amounts shown to logged-out visitors, who can't call the
 * authenticated earn endpoints (both require `checkValidUser`). Logged-in customers —
 * member or not — get live amounts from calc-earn; this is only used when there is no
 * customerId. Keep in sync with the `LuxPlusRenewal*` reward configs in svc-lux-loyalty.
 */
export const LUX_PLUS_RENEWAL_FALLBACK_AMOUNTS: Record<
  LuxPlusRenewalAction,
  { points: number; statusCredits: number }
> = {
  LuxPlusRenewal: { points: 7500, statusCredits: 100 },
  LuxPlusRenewal2YR: { points: 7500, statusCredits: 150 },
  LuxPlusRenewal3YR: { points: 7500, statusCredits: 200 },
}

/**
 * Total renewal Status Credits for a reward. svc-lux-loyalty may store the amount as a base
 * `statusCredits`, a `bonusStatusCredits`, or both, so the displayed total is always their sum.
 * Shared by the completed-history and upcoming/earn paths so the two never diverge (a reward
 * with only a base amount must not render as 0 on the upcoming path).
 */
export function sumRenewalStatusCredits(
  statusCredits: number | undefined,
  bonusStatusCredits: number | undefined,
): number {
  return (statusCredits ?? 0) + (bonusStatusCredits ?? 0)
}

/**
 * The renewal-reward year the member is currently earning towards — which rung of the
 * 1st/2nd/3rd renewal ladder is "current". Rewards are earned *on renewal*, so:
 *  - ACTIVE members: the in-progress year (`renewalRewardEligibleYears + 1`), earned at their next renewal.
 *  - members awaiting renewal / within grace: the just-completed year they're renewing from
 *    (`renewalRewardEligibleYears`), whose reward lands on that pending renewal.
 * Returns 0 for non-members / fully lapsed (no current renewal).
 */
export function getNextLuxPlusRenewalRewardYear(
  subscription: App.MemberSubscriptionItem | undefined,
): number {
  if (!subscription) return 0

  const completedYears = subscription.renewalRewardEligibleYears ?? 0
  // A free-preview subscription is "ACTIVE" but isn't a paid period that renews, so it earns no
  // renewal reward — treat it like a non-member (keeps this in step with the paid-membership
  // selector, getHasPaidLuxPlusMembershipInCurrentRegion, which also excludes free_preview).
  const hasActivePaidPeriod = subscription.status === 'ACTIVE' && subscription.tier !== 'free_preview'

  return hasActivePaidPeriod ? completedYears + 1 : completedYears
}

/**
 * Maps a renewal year to its staggered action identifier, capping at the year 3
 * reward. Returns `null` below year 1 (no renewal reward yet).
 */
export function getLuxPlusRenewalActionIdentifier(
  renewalRewardYear: number,
): LuxPlusRenewalAction | null {
  if (renewalRewardYear < 1) return null

  return LUX_PLUS_RENEWAL_ACTIONS[Math.min(renewalRewardYear, LUX_PLUS_RENEWAL_ACTIONS.length) - 1]
}

/** The renewal action identifier the given member is currently earning towards. */
export function getCurrentLuxPlusRenewalActionIdentifier(
  subscription: App.MemberSubscriptionItem | undefined,
): LuxPlusRenewalAction | null {
  return getLuxPlusRenewalActionIdentifier(getNextLuxPlusRenewalRewardYear(subscription))
}

/**
 * `completed` — an earned past renewal (amount comes from action-reward history).
 * `current`   — the renewal the member is next earning towards (the "You're almost here" step).
 * `upcoming`  — a future renewal not yet reached.
 * Amounts for `current`/`upcoming` come from the action-reward earn calc.
 */
export type LuxPlusRenewalStepState = 'completed' | 'current' | 'upcoming'

export interface LuxPlusRenewalStep {
  /** 1-based position in the 1st / 2nd / 3rd+ timeline. */
  year: number
  identifier: LuxPlusRenewalAction
  state: LuxPlusRenewalStepState
}

/**
 * Classifies the fixed 3-step timeline (1st / 2nd / 3rd+) for a given renewal year.
 * `nextRenewalRewardYear < 1` (non-member / lapsed) yields no current step — every step is `upcoming`.
 * The 3rd step is the cap: it is `current` for year 3 and every year after (it repeats),
 * never `completed`.
 */
export function classifyLuxPlusRenewalSteps(nextRenewalRewardYear: number): Array<LuxPlusRenewalStep> {
  const currentIndex = nextRenewalRewardYear >= 1 ? Math.min(nextRenewalRewardYear, LUX_PLUS_RENEWAL_ACTIONS.length) : 0

  return LUX_PLUS_RENEWAL_ACTIONS.map((identifier, index): LuxPlusRenewalStep => {
    const year = index + 1
    let state: LuxPlusRenewalStepState = 'upcoming'
    if (currentIndex) {
      if (year < currentIndex) state = 'completed'
      else if (year === currentIndex) state = 'current'
    }
    return { year, identifier, state }
  })
}
